import { useState } from "react";
import { DISPLAY, WIZARD_TEMPLATES } from "../constants.js";
import { SMALL_FORMATS } from "../lib/display/doc.js";
import { layoutFor } from "../lib/display/templates.js";
import { charCount, findBannedWord } from "../lib/text/validate.js";
import DeliveryFields from "./DeliveryFields.js";
import ImageUploadButton from "./ImageUploadButton.js";
import RichTextDialog from "./RichTextDialog.js";
import "./ComposeFields.css";
import "./ComposeLayers.css";

export default function EditingPanel({
  doc,
  editingSlots,
  editingHidden,
  selectedSize,
  identity,
  references,
  regulated,
  editable,
  budgetFor,
  setSlot,
  toggleBaked,
  bakedSet,
  onToggleHidden,
  onSetDeliveryProfile,
  onSetAnimationPreset,
  onReplay,
  onUploadImage,
  isUploadingImage,
  uploadError,
}) {
  const palette = identity?.palette ?? [];
  const banned = identity?.bannedWords ?? [];
  const logos = (references ?? []).filter((r) => r.kind === "logo");
  const pickableImages = (references ?? []).filter((r) => r.kind === "product" || r.kind === "example");
  // imageAsBackground/image are set per-template, unconditionally across
  // every size - safe to check via any one size in doc.sizes.
  const sampleLayout = doc.sizes[0] ? layoutFor(doc.templateId, doc.sizes[0]) : null;
  const hasImageSlot = !!(sampleLayout?.imageAsBackground || sampleLayout?.image);
  const templateName = WIZARD_TEMPLATES.find((t) => t.id === doc.templateId)?.name ?? doc.templateId;

  return (
    <div className="editing-panel">
      <Section title="Logo">
        {logos.length === 0 ? (
          <p className="editing-panel__hint">No logo uploaded — add one in References.</p>
        ) : (
          <div className="editing-panel__swatch-grid">
            {logos.map((logo) => (
              <button
                key={logo.id}
                type="button"
                disabled={!editable}
                onClick={() => setSlot("logoRefId", logo.id)}
                className={`editing-panel__logo-btn${
                  editingSlots.logoRefId === logo.id ? " editing-panel__logo-btn--selected" : ""
                }`}
              >
                <img src={logo.url} alt={logo.label ?? "Logo"} className="editing-panel__logo-img" />
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Image">
        {!hasImageSlot ? (
          <p className="editing-panel__hint">
            {templateName} doesn't have an image slot — use <strong>Customize layout</strong> below
            to place an image freely.
          </p>
        ) : (
          <>
            <ImageUploadButton
              kind="example"
              onUploadImage={onUploadImage}
              isUploadingImage={isUploadingImage}
              onUploaded={(source) => setSlot("imageSource", source)}
              disabled={!editable}
            />
            {uploadError ? <p className="editing-panel__error">{uploadError}</p> : null}

            {pickableImages.length === 0 ? (
              <p className="editing-panel__hint">
                No product/example references yet — upload one above, or add one in References.
              </p>
            ) : (
              <div className="editing-panel__swatch-grid">
                <button
                  type="button"
                  disabled={!editable}
                  onClick={() => setSlot("imageSource", null)}
                  className={`editing-panel__logo-btn${
                    !editingSlots.imageSource ? " editing-panel__logo-btn--selected" : ""
                  }`}
                >
                  <span className="editing-panel__none-label">None</span>
                </button>
                {pickableImages.map((image) => {
                  const selected =
                    editingSlots.imageSource?.kind === "reference" &&
                    editingSlots.imageSource?.id === image.id;
                  return (
                    <button
                      key={image.id}
                      type="button"
                      disabled={!editable}
                      onClick={() => setSlot("imageSource", { kind: "reference", id: image.id })}
                      className={`editing-panel__logo-btn${
                        selected ? " editing-panel__logo-btn--selected" : ""
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.label ?? "Reference image"}
                        className="editing-panel__logo-img"
                      />
                    </button>
                  );
                })}
              </div>
            )}
            <p className="editing-panel__hint editing-panel__hint--muted">
              AI-generated images come in a later pass — upload directly or pick from your
              uploaded References for now.
            </p>
          </>
        )}
      </Section>

      <Section title="Copy">
        <CopyField
          label="Headline"
          value={editingSlots.headline}
          budget={budgetFor("headline")}
          banned={banned}
          baked={bakedSet.has("headline")}
          editable={editable}
          onToggleBaked={() => toggleBaked("headline")}
          onChange={(v) => setSlot("headline", v)}
          richText
          colorRanges={editingSlots.headlineColorRanges}
          words={editingSlots.headlineWords}
          palette={palette}
          onChangeRich={({ text, colorRanges, words }) => {
            setSlot("headline", text);
            setSlot("headlineColorRanges", colorRanges);
            setSlot("headlineWords", words);
          }}
        />
        <CopyField
          label="Subline"
          value={editingSlots.subline ?? ""}
          budget={budgetFor("subline")}
          banned={banned}
          optional
          baked={bakedSet.has("subline")}
          editable={editable}
          onToggleBaked={() => toggleBaked("subline")}
          onChange={(v) => setSlot("subline", v === "" ? null : v)}
          richText
          colorRanges={editingSlots.sublineColorRanges}
          words={editingSlots.sublineWords}
          palette={palette}
          onChangeRich={({ text, colorRanges, words }) => {
            setSlot("subline", text === "" ? null : text);
            setSlot("sublineColorRanges", colorRanges);
            setSlot("sublineWords", words);
          }}
        />
        <CopyField
          label="CTA"
          value={editingSlots.cta}
          budget={budgetFor("cta")}
          banned={banned}
          baked={bakedSet.has("cta")}
          editable={editable}
          onToggleBaked={() => toggleBaked("cta")}
          onChange={(v) => setSlot("cta", v)}
          richText
          colorRanges={editingSlots.ctaColorRanges}
          words={editingSlots.ctaWords}
          palette={palette}
          onChangeRich={({ text, colorRanges, words }) => {
            setSlot("cta", text);
            setSlot("ctaColorRanges", colorRanges);
            setSlot("ctaWords", words);
          }}
        />

        <div className="editing-panel__field">
          <div className="editing-panel__field-top">
            <span className="editing-panel__field-label">Compliance line</span>
            {regulated ? (
              <span className="editing-panel__locked-badge">Locked — regulated industry</span>
            ) : null}
          </div>
          {regulated ? (
            <p className="editing-panel__locked-value">{doc.slots.complianceLine}</p>
          ) : (
            <input
              type="text"
              value={editingSlots.complianceLine ?? ""}
              maxLength={160}
              placeholder="Optional legal line"
              disabled={!editable}
              onChange={(e) => setSlot("complianceLine", e.target.value === "" ? null : e.target.value)}
              className="editing-panel__input"
            />
          )}
        </div>
      </Section>

      <Section title="Colors">
        <ColorField
          label="Background"
          value={doc.slots.bgColor}
          palette={palette}
          editable={editable}
          onChange={(v) => setSlot("bgColor", v)}
        />
        <ColorField
          label="CTA"
          value={editingSlots.ctaColor}
          palette={palette}
          editable={editable}
          onChange={(v) => setSlot("ctaColor", v)}
        />
        <ColorField
          label="Text"
          value={editingSlots.textColor}
          palette={palette}
          editable={editable}
          onChange={(v) => setSlot("textColor", v)}
        />
      </Section>

      <Section title="Animation">
        <DeliveryFields doc={doc} onChange={onSetDeliveryProfile} disabled={!editable} />
        <div className="editing-panel__preset-row">
          <select
            value={doc.animationPreset}
            aria-label="Animation preset"
            disabled={!editable}
            onChange={(e) => onSetAnimationPreset(e.target.value)}
            className="editing-panel__input"
          >
            {DISPLAY.ANIMATION_PRESETS.map((p) => (
              <option key={p} value={p}>
                {p.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <button type="button" onClick={onReplay} className="compose-page__replay-btn">
            Replay
          </button>
        </div>
        <p className="editing-panel__hint">
          This is the preview default. You pick the motion for each export in the export dialog —
          the same banner can ship with different motions.
        </p>
      </Section>

      {selectedSize && SMALL_FORMATS.includes(selectedSize) ? (
        <Section title="Elements on this size">
          {["subline", "image"].map((element) => {
            const hidden = (editingHidden ?? []).includes(element);
            return (
              <label key={element} className="editing-panel__checkbox-field">
                <input
                  type="checkbox"
                  checked={!hidden}
                  disabled={!editable}
                  onChange={() => onToggleHidden(selectedSize, element)}
                />
                Show {element}
              </label>
            );
          })}
        </Section>
      ) : null}

      {selectedSize ? (
        <p className="editing-panel__scope-note">
          Editing <strong>{selectedSize}</strong> only. Switch to "All sizes" to edit the shared
          version every size falls back to.
        </p>
      ) : (
        <p className="editing-panel__scope-note">
          Editing the shared version. Select one size above to give it its own override instead.
        </p>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="editing-panel__section">
      <h3 className="editing-panel__section-title">{title}</h3>
      <div className="editing-panel__section-body">{children}</div>
    </section>
  );
}

function CopyField({
  label,
  value,
  budget,
  banned,
  optional = false,
  baked,
  editable,
  onToggleBaked,
  onChange,
  richText = false,
  colorRanges,
  words,
  palette,
  onChangeRich,
}) {
  const count = charCount(value);
  const over = count > budget;
  const bannedHit = findBannedWord(value, banned);
  const [textDialogOpen, setTextDialogOpen] = useState(false);

  return (
    <div className="editing-panel__field">
      <div className="editing-panel__field-top">
        <span className="editing-panel__field-label">
          {label}
          {optional ? <span className="editing-panel__optional"> (optional)</span> : null}
        </span>
        <span className="editing-panel__field-top-right">
          <span className="editing-panel__baked-toggle">
            <button
              type="button"
              disabled={!editable}
              onClick={() => baked && onToggleBaked()}
              aria-pressed={!baked}
              className={`editing-panel__baked-btn${!baked ? " editing-panel__baked-btn--active" : ""}`}
            >
              Platform
            </button>
            <button
              type="button"
              disabled={!editable}
              onClick={() => !baked && onToggleBaked()}
              aria-pressed={baked}
              title="The text is already in your uploaded image — the platform renders nothing here."
              className={`editing-panel__baked-btn${baked ? " editing-panel__baked-btn--active" : ""}`}
            >
              In image
            </button>
          </span>
          {!baked ? (
            <span className={`editing-panel__count${over ? " editing-panel__count--over" : ""}`}>
              {count}/{budget}
            </span>
          ) : null}
        </span>
      </div>
      {baked ? (
        <p className="editing-panel__baked-note">
          Supplied by your uploaded image — the platform won't render this text.
        </p>
      ) : richText ? (
        <>
          <div className="layers-panel__text-preview">
            {value.split("\n").map((line, i, arr) => (
              <span key={i}>
                {line || <em>(empty line)</em>}
                {i < arr.length - 1 ? <br /> : null}
              </span>
            ))}
            {(words?.length > 0 && words.some((w) => w.color)) || colorRanges?.length > 0 ? (
              <span className="layers-panel__text-preview-color-dot" aria-label="Has colored text" />
            ) : null}
          </div>
          <button
            type="button"
            disabled={!editable}
            onClick={() => setTextDialogOpen(true)}
            className="layers-panel__link-btn"
          >
            Edit text →
          </button>
          {textDialogOpen ? (
            <RichTextDialog
              title={`Editing ${label} text`}
              initialText={value}
              initialColorRanges={colorRanges ?? []}
              initialWords={words ?? []}
              paletteColors={palette ?? []}
              onSave={(result) => {
                onChangeRich(result);
                setTextDialogOpen(false);
              }}
              onClose={() => setTextDialogOpen(false)}
            />
          ) : null}
          {bannedHit ? (
            <p className="editing-panel__banned-note">
              Contains blocked word: <mark>{bannedHit}</mark>
            </p>
          ) : null}
        </>
      ) : (
        <>
          <input
            type="text"
            value={value}
            disabled={!editable}
            onChange={(e) => onChange(e.target.value)}
            className={`editing-panel__input${over || bannedHit ? " editing-panel__input--error" : ""}`}
          />
          {bannedHit ? (
            <p className="editing-panel__banned-note">
              Contains blocked word: <mark>{bannedHit}</mark>
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

export function ColorField({ label, value, palette, editable, onChange }) {
  const offPalette = palette.length > 0 && !palette.some((p) => p.toLowerCase() === value.toLowerCase());

  return (
    <div className="editing-panel__field">
      <div className="editing-panel__field-top">
        <span className="editing-panel__field-label">{label}</span>
        {offPalette ? <span className="editing-panel__off-palette">off-palette</span> : null}
      </div>
      <div className="editing-panel__color-row">
        {palette.map((hex) => (
          <button
            key={hex}
            type="button"
            disabled={!editable}
            onClick={() => onChange(hex)}
            aria-label={hex}
            className={`editing-panel__swatch${
              hex.toLowerCase() === value.toLowerCase() ? " editing-panel__swatch--selected" : ""
            }`}
            style={{ background: hex }}
          />
        ))}
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000"}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value)}
          className="editing-panel__color-input"
        />
        <input
          type="text"
          value={value}
          disabled={!editable}
          onChange={(e) => onChange(e.target.value)}
          className="editing-panel__hex-input"
        />
      </div>
    </div>
  );
}